import React, { useCallback, useEffect, useState } from "react";
import {
    ArrowLeftOutlined,
    CloseOutlined,
    LeftOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import "../../styles/portfolio-lightbox.css";

export type PortfolioLightboxItem = {
    src: string;
    alt: string;
};

type PortfolioImageLightboxProps = {
    items: PortfolioLightboxItem[];
    activeIndex: number;
    onClose: () => void;
    onIndexChange: (index: number) => void;
};

const slideEase = [0.22, 1, 0.36, 1] as const;

export const PortfolioImageLightbox = ({
    items,
    activeIndex,
    onClose,
    onIndexChange,
}: PortfolioImageLightboxProps) => {
    const reduceMotion = useReducedMotion();
    const [direction, setDirection] = useState(0);

    const goTo = useCallback(
        (nextIndex: number, slideDirection: number) => {
            setDirection(slideDirection);
            onIndexChange(nextIndex);
        },
        [onIndexChange]
    );

    const goNext = useCallback(() => {
        goTo((activeIndex + 1) % items.length, 1);
    }, [activeIndex, goTo, items.length]);

    const goPrev = useCallback(() => {
        goTo((activeIndex - 1 + items.length) % items.length, -1);
    }, [activeIndex, goTo, items.length]);

    useEffect(() => {
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
                return;
            }
            if (event.key === "ArrowRight") {
                event.preventDefault();
                goNext();
                return;
            }
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                goPrev();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [goNext, goPrev, onClose]);

    const slideOffset = reduceMotion ? 0 : 72;
    const imageVariants = {
        enter: (slideDirection: number) => ({
            x: slideDirection > 0 ? slideOffset : slideDirection < 0 ? -slideOffset : 0,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (slideDirection: number) => ({
            x: slideDirection > 0 ? -slideOffset : slideDirection < 0 ? slideOffset : 0,
            opacity: 0,
        }),
    };

    const activeItem = items[activeIndex];

    return (
        <motion.div
            className="portfolio-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Portfolio image viewer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: slideEase }}
            onClick={onClose}
        >
            <button
                type="button"
                className="portfolio-lightbox__back"
                onClick={onClose}
                aria-label="Back to gallery"
            >
                <ArrowLeftOutlined />
            </button>

            <button
                type="button"
                className="portfolio-lightbox__close"
                onClick={onClose}
                aria-label="Close gallery"
            >
                <CloseOutlined />
            </button>

            <p className="portfolio-lightbox__counter" aria-live="polite">
                {activeIndex + 1} / {items.length}
            </p>

            <div
                className="portfolio-lightbox__stage"
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className="portfolio-lightbox__nav portfolio-lightbox__nav--prev"
                    onClick={goPrev}
                    aria-label="Previous image"
                >
                    <LeftOutlined />
                </button>

                <div className="portfolio-lightbox__frame">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.img
                            key={activeIndex}
                            src={activeItem.src}
                            alt={activeItem.alt}
                            className="portfolio-lightbox__img"
                            custom={direction}
                            variants={imageVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                duration: reduceMotion ? 0 : 0.32,
                                ease: slideEase,
                            }}
                        />
                    </AnimatePresence>
                </div>

                <button
                    type="button"
                    className="portfolio-lightbox__nav portfolio-lightbox__nav--next"
                    onClick={goNext}
                    aria-label="Next image"
                >
                    <RightOutlined />
                </button>
            </div>
        </motion.div>
    );
};
